import { useNavigation } from "@react-navigation/native";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import { openBrowserAsync } from "expo-web-browser";
import { TicketDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { barcodeHoursBefore } from "utils/ticketUtils";
import { flightClubPlanIds } from "./helpers";

interface EventDetailFooterProps {
  tickets: TicketDetails[];
  groupCode: string;
}

const planMap = new Map<number, string>(
  flightClubPlanIds.map((id) => [id, "SHOW PASS"]),
);

const flightClubDeliveryText = "Show your Pass for entry to the Flight Club";
const deliveryTextMap = new Map<number, string>(
  flightClubPlanIds.map((id) => [id, flightClubDeliveryText]),
);

const OpenLink = ({ text, onPress }: { text: string; onPress: () => void }) => (
  <Pressable onPress={onPress}>
    <Text status="info" category="c1">
      {text}
    </Text>
  </Pressable>
);

const EventDetailFooter: React.FC<EventDetailFooterProps> = ({
  tickets,
  groupCode,
}) => {
  const { barcodeMinutesBefore, barcodeReady, barcodeProvider, event } =
    tickets[0];
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const navigation: any = useNavigation();

  // if the value is set to FANRALLY, then we are able to display barcodes within our UI.
  const barcodesDeliveredInternally = barcodeProvider === "FANRALLY";

  const { account } = useAccount();
  const openBallParkApp = () => {
    openBrowserAsync("https://bp.mlb.com/wallet");
  };

  const hasTransferableTicket = tickets.some(
    (ticket) => ticket.transferability !== "NONE",
  );

  // https://github.com/FanRally/fanrally-core/issues/1740
  const resolveBarcodeButtonText = () => {
    const buttonText = planMap.get(tickets[0].planPackageId);
    if (buttonText) {
      return buttonText;
    }
    return "SHOW BARCODES";
  };

  // https://github.com/FanRally/fanrally-core/issues/1740
  const resolveBarcodeDeliveryText = () => {
    const deliveryText = deliveryTextMap.get(tickets[0].planPackageId);
    if (deliveryText) {
      return deliveryText;
    }
    return i18n.t("screens.EventDetail.barcodeMinutesBefore", {
      barcodeMinutesBefore: barcodeHoursBefore(barcodeMinutesBefore),
    });
  };

  const BarcodesDeliveredToBallpark = () => (
    <>
      <Text>
        Your entry barcodes will be delivered to your mobile device in the{" "}
      </Text>
      <OpenLink text="Ballpark App" onPress={openBallParkApp} />,{" "}
      <Text>inside the following MLB Ballpark account:</Text>
      <Text category="s2">{account?.extUserDetails?.tdcUsername!}</Text>
    </>
  );

  const handleShowBarcodes = () => {
    navigation.navigate("Barcode", { groupCode });
  };

  const BarcodesDeliveredToAXS = () => (
    <>
      <Text>
        Your tickets will be available within the{" "}
        {tenantConfig?.mobileAppName || tickets[0].event.tenantAlias} app in the
        following AXS account:
      </Text>
      <Text category="s2">{account?.extUserDetails?.axsUsername!}</Text>
      <br />
      <Text>NOTE: Once transferred, this booking is final.</Text>
    </>
  );

  const BarcodesDeliveredToTFL = () => (
    <Text>
      Your tickets will be delivered before your event. You'll receive an email
      from the venue's ticketing platform.
    </Text>
  );

  const BarcodesDeliveredExternally = () => {
    return (
      <>
        {barcodeProvider === "TDC" && <BarcodesDeliveredToBallpark />}
        {barcodeProvider === "AXS" && <BarcodesDeliveredToAXS />}
        {barcodeProvider === "TFL" && <BarcodesDeliveredToTFL />}
      </>
    );
  };

  return (
    <>
      {barcodesDeliveredInternally ? (
        !event.showBarcodesButtonHidden && (
          <View>
            <Button
              style={{ marginVertical: 15, width: "80%", alignSelf: "center" }}
              onPress={handleShowBarcodes}
              disabled={!barcodeReady}
              status={barcodeReady ? "primary" : "basic"}
            >
              {resolveBarcodeButtonText()}
            </Button>
          </View>
        )
      ) : (
        <Text category="c1" style={{ textAlign: "center" }}>
          <BarcodesDeliveredExternally />
        </Text>
      )}

      {barcodeProvider === "FANRALLY" && !event.showBarcodesButtonHidden && (
        <Text style={[style.barcodeText, { fontWeight: "700" }]}>
          {resolveBarcodeDeliveryText()}
        </Text>
      )}

      {!hasTransferableTicket && !tenantConfig?.hideTransferabilityText && (
        <Text category="c1" style={{ textAlign: "center", marginTop: 10 }}>
          {i18n.t("screens.EventDetail.reservationsNonTransferable")}
        </Text>
      )}
    </>
  );
};

const style = StyleSheet.create({
  iconContainer: {
    borderWidth: 1,
    borderRadius: 36,
    padding: 6,
    borderColor: "rgb(187,187,187)",
  },
  barcodeText: {
    paddingHorizontal: 15,
    textAlign: "center",
    marginBottom: 15,
  },
});
export default EventDetailFooter;
